import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { billingApi, profileApi } from '@pukka/api';
import { hookUtils } from '@pukka/common/utils';
import { ToastContainer } from '@pukka/toast';
import { useFlagWithFallback } from '@pukka/unleash';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import Store from 'store';
import { twMerge } from 'tailwind-merge';

import { BillingReminder } from '../BillingReminder';
import { MarketingModal } from '../Modals';
import { Sidebar } from '../Sidebar';

import { ReminderDetail, getBillingReminderDetail } from './utils/getBillingReminderDetail';

interface MainLayoutProps {
  readonly hideMarketing?: boolean;
  readonly forceMobile?: boolean;
}

function MainLayout({ hideMarketing, forceMobile }: MainLayoutProps) {
  const { data: profile } = profileApi.useProfile();
  const main = useRef<HTMLDivElement>(null);
  const rect = hookUtils.useRect(main);
  const location = useLocation();
  const isMobile = hookUtils.useIsMobile() || forceMobile;
  const [enableRecurringPayment] = useFlagWithFallback('recurring-payment-backoffice');
  const { data: activeRecurring } = billingApi.useActiveRecurringPayment();

  const [isBannerOpen, setIsBannerOpen] = useState(true);
  const reminderDetail = getBillingReminderDetail(profile, isBannerOpen, activeRecurring, enableRecurringPayment);
  const isOnBillingPage = location.pathname?.startsWith('/v2/settings/billing');
  const shouldShowBillingReminder = !!reminderDetail && (!isOnBillingPage || reminderDetail.type !== 'discount');

  const handleCloseBanner = (type: ReminderDetail['type']) => {
    setIsBannerOpen(false);

    if (type === 'discount') {
      Store.set('hasCloseBillingDiscountBanner', true);
    }
  };

  useEffect(() => {
    if (reminderDetail?.type === 'expired' && !isOnBillingPage) {
      window.location.replace('/v2/settings/billing');
    }
  }, [reminderDetail, isOnBillingPage]);

  const isTransactionPage = location.pathname?.startsWith('/v2/reports/transactions');
  const isBrandsPage = location.pathname?.startsWith('/v2/library/brands');
  const isCustomerDisplayCampaignPage = location.pathname?.startsWith('/v2/customer-display/campaign');

  return (
    <TooltipProvider>
      <hookUtils.MainLayoutContext.Provider value={rect}>
        <div className={twMerge('flex h-0 w-full flex-1 flex-col md:flex-row')}>
          {!hideMarketing ? <MarketingModal /> : null}
          <Sidebar />
          <div className={twMerge('relative flex w-full flex-1 flex-col overflow-scroll md:w-0 md:overflow-visible')}>
            {shouldShowBillingReminder && (
              <BillingReminder
                type={reminderDetail?.type}
                text={reminderDetail?.text}
                isOwner={profile?.is_owner}
                businessId={profile?.business?.id}
                expiredAt={profile?.business?.expired_at}
                onCloseBanner={handleCloseBanner}
              />
            )}
            <main
              ref={main}
              className={twMerge(
                'relative flex w-full flex-1 overflow-auto',
                isMobile && (isTransactionPage || isBrandsPage || isCustomerDisplayCampaignPage) ? 'w-[1280px]' : '',
              )}
            >
              <Outlet />
            </main>
            <ToastContainer closeOnClick newestOnTop={false} hideProgressBar />
          </div>
        </div>
      </hookUtils.MainLayoutContext.Provider>
    </TooltipProvider>
  );
}

export { MainLayout };
